import React, { useState } from 'react';
import styled from 'styled-components';
import GoogleMapReact from 'google-map-react';
import {
  colors,
  fonts,
  radii,
  breakpoints,
  headingStyle,
  sectionPaddingStyle,
  containerStyle,
  isOpenNowFromHoraires,
} from '../annuaire/designSystem';
import { googleKey as GOOGLE_MAPS_KEY } from '../../utils/utils';
import { adresseMasquee } from '../../annuaire/adressePublique';
import {
  fixerLiteHasOpeningHours,
  fixerLiteOpenNow,
} from '../../annuaire/bookingState';

const DEFAULT_ZOOM = 15;

const MAP_OPTIONS = {
  scrollwheel: false,
  fullscreenControl: false,
  zoomControl: true,
};

const Section = styled.section`
  margin: 0;
  ${sectionPaddingStyle}
  background-color: ${colors.vertPastel};
`;

const Container = styled.div`
  ${containerStyle}
`;

const Heading = styled.h2`
  ${headingStyle}
  font-size: 1.5rem;
  margin-bottom: 24px;

  @media (min-width: ${breakpoints.mobile}) {
    font-size: 2.125rem;
  }
`;

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 66.66%;
  overflow: hidden;
  border-radius: ${radii.card};
`;

const MapInner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const PinWrapper = styled.div`
  position: absolute;
  transform: translate(-50%, -100%);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PinBubble = styled.div`
  width: 52px;
  height: 52px;
  background: ${colors.white};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const PinImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const PinArrow = styled.div`
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid ${colors.white};
  margin-top: -1px;
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.1));
`;

const InfoBubble = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 6px;
  background: ${colors.white};
  border-radius: ${radii.small};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 16px;
  min-width: 220px;
  max-width: 280px;
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid ${colors.white};
  }
`;

const InfoName = styled.div`
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 0.95rem;
  color: ${colors.textDark};
  margin-bottom: 4px;
`;

const InfoAddress = styled.div`
  font-family: ${fonts.body};
  font-size: 0.85rem;
  color: ${colors.textSecondary};
  margin-bottom: 10px;
  line-height: 1.4;
`;

const InfoRating = styled.div`
  font-family: ${fonts.body};
  font-size: 0.85rem;
  color: ${colors.textDark};
  margin-bottom: 6px;

  span {
    color: #f5a623;
  }
`;

const InfoStatus = styled.div`
  font-family: ${fonts.body};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(props) => (props.isOpen ? '#2e7d32' : '#c62828')};
  margin-bottom: 6px;
`;

const InfoPhone = styled.a`
  display: block;
  font-family: ${fonts.body};
  font-size: 0.85rem;
  color: ${colors.textSecondary};
  text-decoration: none;
  margin-bottom: 10px;

  &:hover {
    color: ${colors.textDark};
  }
`;

const InfoLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoLink = styled.a`
  font-family: ${fonts.body};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${colors.bleu} !important;
  text-decoration: none;

  &:hover {
    color: ${colors.bleuFonce} !important;
    opacity: 1;
  }
`;

function ShopPin({ shop, lat, lng }) {
  const [showInfo, setShowInfo] = useState(false);

  const details = shop.details || {};
  const noteGoogle = details.note_google;
  const nbAvis = details.nb_avis_google;
  const phone = details.telephone_google;

  let statut = details.statut_etablissement || null;
  if (statut === 'Ouvert') {
    // FIXERLIGHT-068 : même repli que le tableau d'horaires et le bandeau de
    // tête — sans lui, cette bulle pouvait afficher « Ouvert maintenant » sur
    // un atelier Fixer Lite dont le tableau, lui, dit « Non renseigné ». Le
    // garde est `fixerLiteHasOpeningHours`, pas `isFixerLiteEnabled` seul :
    // un compte activé sans gabarit réglé retombe entièrement sur Google.
    const openNow = fixerLiteHasOpeningHours(shop)
      ? fixerLiteOpenNow(shop)
      : isOpenNowFromHoraires(details.horaires);
    if (openNow === true) statut = 'Ouvert';
    else if (openNow === false) statut = 'Fermé';
  }

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${shop.name || ''} ${shop.address || ''}`,
  )}`;

  return (
    <PinWrapper onClick={() => setShowInfo((v) => !v)}>
      {showInfo && (
        <InfoBubble onClick={(e) => e.stopPropagation()}>
          <InfoName>{shop.name}</InfoName>
          {shop.address && <InfoAddress>{shop.address}</InfoAddress>}
          {noteGoogle && (
            <InfoRating>
              <span>★</span> {noteGoogle}/5 — {nbAvis || 0} avis
            </InfoRating>
          )}
          {statut && (
            <InfoStatus isOpen={statut === 'Ouvert'}>
              {statut === 'Ouvert' ? 'Ouvert maintenant' : 'Fermé'}
            </InfoStatus>
          )}
          {phone && <InfoPhone href={`tel:${phone}`}>{phone}</InfoPhone>}
          <InfoLinks>
            <InfoLink
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer">
              Voir sur Google Maps
            </InfoLink>
            <InfoLink
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer">
              Itinéraire →
            </InfoLink>
          </InfoLinks>
        </InfoBubble>
      )}
      <PinBubble>
        <PinImage
          src="/static/img/annuaire/icones/ROULEZJEUNESSE_ICONES__SHOP.png"
          alt="shop"
        />
      </PinBubble>
      <PinArrow />
    </PinWrapper>
  );
}

export default function ShopMap({ shop }) {
  const lat = parseFloat(shop.latitude || shop.lat);
  const lng = parseFloat(shop.longitude || shop.lng);

  // FIXERLIGHT-268 : sans adresse publiée, pas d'épingle — les coordonnées
  // servies sont arrondies et ne désignent aucune porte.
  if (!lat || !lng || adresseMasquee(shop)) return null;

  return (
    <Section>
      <Container>
        <Heading>Localisation</Heading>
        <MapContainer>
          <MapInner>
            <GoogleMapReact
              bootstrapURLKeys={{ key: GOOGLE_MAPS_KEY }}
              defaultCenter={{ lat, lng }}
              defaultZoom={DEFAULT_ZOOM}
              options={MAP_OPTIONS}>
              <ShopPin lat={lat} lng={lng} shop={shop} />
            </GoogleMapReact>
          </MapInner>
        </MapContainer>
      </Container>
    </Section>
  );
}
